import React from 'react';
import { createBrowserRouter, RouterProvider, useLocation, useNavigate, Outlet } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { LandingPage } from './pages/LandingPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { Dashboard } from './pages/Dashboard';
import { AnalysisPage } from './pages/AnalysisPage';
import { AdGeneratorPage } from './pages/AdGeneratorPage';
import { ReportsPage } from './pages/ReportsPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { TeamPage } from './pages/TeamPage';
import { CreditsPage } from './pages/CreditsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ContactPage } from './pages/ContactPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { RecentAnalysisPage } from './pages/RecentAnalysisPage';
import { SearchPage } from './pages/SearchPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { BlogAdminPage } from './pages/BlogAdminPage';
import { BlogEditorPage } from './pages/BlogEditorPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SearchProvider } from './contexts/SearchContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { Navbar } from './components/layout/Navbar';
import { Sidebar } from './components/layout/Sidebar';
import { AuthErrorBoundary } from './components/AuthErrorBoundary';
import { SessionTimeoutWarning } from './components/SessionTimeoutWarning';
import { ScrollToTop } from './components/ScrollToTop';
import InvitationAcceptancePage from './pages/InvitationAcceptancePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import { HelmetProvider } from 'react-helmet-async';
import GTMService from './lib/gtm';
import { UnifiedCreditService } from './lib/unified-credit-service';

// Legacy redirect component for old credits URL
function CreditsRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    navigate(`/dashboard/credits${location.search}`, { replace: true });
  }, [navigate, location.search]);

  return null;
}

// Login redirect component
function LoginRedirect() {
  const navigate = useNavigate();

  React.useEffect(() => {
    navigate('/onboarding?mode=login', { replace: true });
  }, [navigate]);

  return null;
}

// Register redirect component
function RegisterRedirect() {
  const navigate = useNavigate();

  React.useEffect(() => {
    navigate('/onboarding?mode=register', { replace: true });
  }, [navigate]);

  return null;
}

// Enhanced Protected Route Component with route preservation
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, hasSubscriptionPlan } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // **FIX: Move ALL hooks to the top of the component - Rules of Hooks**
  const stableUserRef = React.useRef<typeof user>(null);
  
  // Special handling for payment flow - don't redirect during payment processing
  const isPaymentFlow = location.pathname === '/payment/success' || location.pathname === '/payment/cancel' || location.pathname === '/payment/failure';
  
  // Track navigation state to prevent redundant redirects
  const [isNavigating, setIsNavigating] = React.useState(false);
  const navigationTargetRef = React.useRef<string | null>(null);
  
  // Only update stable reference when user ID actually changes (not on every user object change)
  React.useEffect(() => {
    if (user?.id && stableUserRef.current?.id !== user.id) {
      stableUserRef.current = user;
    }
  }, [user]);
  
  // **FIX: Stable subscription check with proper hook usage**
  const hasValidSubscription = React.useMemo(() => {
    // During payment flows, always return true to allow access
    if (isPaymentFlow) {
      return true;
    }
    
    // If no user or still loading, return false
    if (!user || isLoading) {
      return false;
    }
    
    try {
      // The hasSubscriptionPlan function now properly handles team members
      const result = hasSubscriptionPlan();
      
      
      return result;
    } catch (error) {
      console.error('❌ Error checking subscription:', error);
      return false;
    }
  }, [
    user,
    hasSubscriptionPlan,
    isPaymentFlow,
    isLoading,
    location.pathname
  ]);

  // Handle redirects programmatically with navigation guards
  React.useEffect(() => {
    if (isLoading || isNavigating) return;

    const currentPath = location.pathname + location.search;
    
    // Don't redirect if we're already going to the right place or just came from payment success
    if (navigationTargetRef.current === currentPath) {
      return;
    }
    
    // Special handling for payment success flow - don't interfere
    if (location.pathname === '/payment/success' || location.search.includes('onboarding=true')) {
      console.log('🎯 App.tsx: Allowing payment success flow, not redirecting');
      return;
    }

    // Redirect to login if no user, but preserve intended destination
    if (!user) {
      const returnTo = location.pathname !== '/dashboard' ? location.pathname + location.search : '';
      const loginUrl = `/onboarding?mode=login${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`;
      
      // Prevent duplicate navigation
      if (currentPath === loginUrl) return;
      
      console.log('🔄 App.tsx: Redirecting to login:', loginUrl);
      setIsNavigating(true);
      navigationTargetRef.current = loginUrl;
      navigate(loginUrl, { replace: true });
      return;
    }

    // Allow payment flows without subscription check - but don't interfere with their navigation
    if (isPaymentFlow) {
      return;
    }

    // Check subscription plan only if user is fully loaded and not in payment flow
    if (user && !hasValidSubscription) {
      const returnTo = location.pathname !== '/dashboard' ? location.pathname + location.search : '';
      const subscriptionUrl = `/onboarding?step=subscription${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`;
      
      // Prevent duplicate navigation
      if (currentPath === subscriptionUrl) return;
      
      console.log('🔄 App.tsx: Redirecting to subscription:', subscriptionUrl);
      setIsNavigating(true);
      navigationTargetRef.current = subscriptionUrl;
      navigate(subscriptionUrl, { replace: true });
      return;
    }
  }, [user, isLoading, hasValidSubscription, isPaymentFlow, location.pathname, location.search, navigate, isNavigating]);
  
  // Reset navigation state when location changes
  React.useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => {
        setIsNavigating(false);
        navigationTargetRef.current = null;
        console.log('🎯 App.tsx: Navigation state reset');
      }, 500); // Slightly longer delay to ensure navigation completes
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isNavigating]);
  
  // Show enhanced loading while authentication is being initialized OR user profile is loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600 mx-auto"></div>
          <div className="mt-4 text-gray-600">
            {isPaymentFlow ? 'Processing payment...' : 'Loading your account...'}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {location.pathname !== '/dashboard' && `Preparing ${location.pathname}`}
          </div>
        </div>
      </div>
    );
  }

  // Don't render children if redirecting
  if (!user || (!isPaymentFlow && !hasValidSubscription)) {
    return null;
  }

  return <>{children}</>;
}

// Dashboard Layout Component
function DashboardLayout() {
  const [showSidebar, setShowSidebar] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState(false);

  // Check if screen is mobile and update state
  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      if (mobile) {
        setShowSidebar(false); // Hide sidebar by default on mobile
      } else {
        setShowSidebar(true); // Show sidebar by default on desktop
      }
    };

    // Check on mount
    checkMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const closeSidebar = () => {
    setShowSidebar(false);
  };

  return (
    <div className="flex min-h-screen">
      {/* Unified sidebar that handles both mobile and desktop */}
      <Sidebar 
        isOpen={showSidebar} 
        onClose={closeSidebar} 
        isMobile={isMobile}
      />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar onToggleSidebar={toggleSidebar} />
        <main className="flex-1 bg-gray-50 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Create Sentry-instrumented router for performance monitoring
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

// Router configuration with future flags
const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <AppWithRouter />,
    children: [
      {
        index: true,
        element: <LandingPage />
      },
      {
        path: "onboarding",
        element: <OnboardingPage />
      },
      {
        path: "login",
        element: <LoginRedirect />
      },
      {
        path: "register",
        element: <RegisterRedirect />
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />
      },
      {
        path: "reset-password",
        element: <ResetPasswordPage />
      },
      {
        path: "team/accept-invitation",
        element: <InvitationAcceptancePage />
      },
      {
        path: "unsubscribe",
        element: <UnsubscribePage />
      },
      {
        path: "credits",
        element: <CreditsRedirect />
      },
      {
        path: "payment/success",
        element: <PaymentSuccessPage />
      },
      {
        path: "payment/cancel",
        element: <PaymentCancelPage />
      },
      {
        path: "payment/failure",
        element: <PaymentFailurePage />
      },
      {
        path: "blog",
        element: <BlogPage />
      },
      {
        path: "blog/:slug",
        element: <BlogPostPage />
      },
      {
        path: "terms",
        element: <TermsOfServicePage />
      },
      {
        path: "privacy",
        element: <PrivacyPolicyPage />
      },
      {
        path: "cookies",
        element: <CookiePolicyPage />
      },
      {
        path: "dashboard",
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Dashboard />
          },
          {
            path: "analysis",
            element: <AnalysisPage />
          },
          {
            path: "ad-generator",
            element: <AdGeneratorPage />
          },
          {
            path: "recent-analyses",
            element: <RecentAnalysisPage />
          },
          {
            path: "reports",
            element: <ReportsPage />
          },
          {
            path: "projects",
            element: <ProjectsPage />
          },
          {
            path: "projects/:projectId",
            element: <ProjectDetailPage />
          },
          {
            path: "team",
            element: <TeamPage />
          },
          {
            path: "credits",
            element: <CreditsPage />
          },
          {
            path: "settings",
            element: <SettingsPage />
          },
          {
            path: "contact",
            element: <ContactPage />
          },
          {
            path: "search",
            element: <SearchPage />
          },
          {
            path: "blog",
            element: <BlogAdminPage />
          },
          {
            path: "blog/new",
            element: <BlogEditorPage />
          },
          {
            path: "blog/edit/:id",
            element: <BlogEditorPage />
          }
        ]
      }
    ]
  }
]);


// Component that wraps router-dependent providers
function AppWithRouter() {
  const location = useLocation();

  // Initialize GTM and track page views
  React.useEffect(() => {
    GTMService.initialize();
    UnifiedCreditService.initialize();
  }, []);

  // Track route changes as page views
  React.useEffect(() => {
    GTMService.trackPageView(document.title);
  }, [location.pathname]);

  return (
    <SearchProvider>
      <div className="min-h-screen bg-gray-50">
        <ScrollToTop />
        <SessionTimeoutWarning />
        <Outlet />
      </div>
    </SearchProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Sentry.ErrorBoundary 
        fallback={({ resetError }) => (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                Application Error
              </h2>
              <p className="text-gray-600 text-center mb-6">
                We're sorry, something went wrong. Our team has been automatically notified.
              </p>
              <button
                onClick={resetError}
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Try Again
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                If the problem persists, please{' '}
                <a href="mailto:support@rulevia.com" className="text-purple-600 hover:underline">
                  contact support
                </a>
              </p>
            </div>
          </div>
        )}
        beforeCapture={(scope) => {
          scope.setTag('errorBoundary', 'app-root');
          scope.setLevel('error');
        }}
      >
        <AuthErrorBoundary>
          <AuthProvider>
            <OnboardingProvider>
              <RouterProvider router={router} />
            </OnboardingProvider>
          </AuthProvider>
        </AuthErrorBoundary>
      </Sentry.ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;